import { BarChart, ProportionBar, StatTile } from '../charts/Charts'
import { reportPill } from '../ui'

interface DimensionRollup {
  incidents: number
  resolved: number
  still_open: number
  vehicles: number
  median_hours: number | null
  max_idle_hours: number | null
  avg_km_away: number | null
  trips_lost: number
  revenue_lost_inr: number
}

export interface BreakdownData {
  totals: DimensionRollup
  by_category: Record<string, DimensionRollup>
  by_severity: Record<string, DimensionRollup>
  by_hub_proximity: Record<string, DimensionRollup>
  fleet_daily_trip_target: number
  target_trips_in_period: number
  revenue_per_trip_inr: number
}

function formatInr(v: number): string {
  return `₹${Math.round(v).toLocaleString('en-IN')}`
}

const SEVERITY_TONE: Record<string, 'ok' | 'warn' | 'danger'> = {
  minor: 'ok', major: 'warn', immobilised: 'danger',
}

export function BreakdownPreview({ data }: { data: BreakdownData }): JSX.Element {
  const t = data.totals
  const categoryPoints = Object.entries(data.by_category)
    .sort(([, a], [, b]) => b.incidents - a.incidents)
    .slice(0, 7)
    .map(([label, r]) => ({ label: label.length > 14 ? `${label.slice(0, 13)}…` : label, title: label, values: { incidents: r.incidents } }))

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
        <StatTile label="Total incidents" value={t.incidents} />
        <StatTile label="Resolved" value={t.resolved} tone="ok" />
        <StatTile label="Still down" value={t.still_open} tone={t.still_open > 0 ? 'danger' : 'default'} />
        <StatTile label="Vehicles affected" value={t.vehicles} />
      </div>

      <div className="rounded-lg border border-slate-100 p-3">
        <h5 className="mb-2 text-[11px] font-semibold text-slate-700">
          Trip &amp; revenue loss from breakdown downtime
        </h5>
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
          <StatTile label="Round trips lost" value={t.trips_lost} tone={t.trips_lost > 0 ? 'danger' : 'default'} />
          <StatTile label="Revenue lost" value={formatInr(t.revenue_lost_inr)} tone={t.revenue_lost_inr > 0 ? 'danger' : 'default'} />
          <StatTile label="Fleet target, this window" value={data.target_trips_in_period} unit="trips" />
        </div>
        <p className="mt-2 text-[10.5px] text-slate-400">
          Each incident's own downtime ÷ 24h (a round trip), at ₹{data.revenue_per_trip_inr.toLocaleString('en-IN')}/trip
          (38 MT standard load × ₹500/MT) — summed across incidents in this window, not a raw hours total.
          Fleet target is {data.fleet_daily_trip_target} round trips/day.
        </p>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-100 p-3">
          <h5 className="mb-2 text-[11px] font-semibold text-slate-700">Resolved vs still down</h5>
          <ProportionBar segments={[
            { label: 'Resolved', value: t.resolved, color: '#16A34A' },
            { label: 'Still down', value: t.still_open, color: '#DC2626' },
          ]} />
          {t.max_idle_hours != null && t.still_open > 0 && (
            <p className="mt-2 text-[11px] text-rose-600">Longest idling now: {t.max_idle_hours}h</p>
          )}
        </div>
        <div className="rounded-lg border border-slate-100 p-3">
          <h5 className="mb-2 text-[11px] font-semibold text-slate-700">By severity</h5>
          <ul className="space-y-1.5">
            {Object.entries(data.by_severity).map(([sev, r]) => (
              <li key={sev} className="flex items-center justify-between text-[11.5px]">
                <span className={reportPill(SEVERITY_TONE[sev] ?? 'neutral')}>{sev}</span>
                <span className="font-mono tabular-nums text-slate-600">
                  {r.incidents} incidents · median {r.median_hours ?? '—'}h · {formatInr(r.revenue_lost_inr)} lost
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="rounded-lg border border-slate-100 p-3">
        <h5 className="mb-2 text-[11px] font-semibold text-slate-700">Incidents by fault category</h5>
        <BarChart data={categoryPoints} series={[{ key: 'incidents', label: 'Incidents', color: '#0EA5E9' }]} />
      </div>

      <div className="rounded-lg border border-slate-100">
        <div className="border-b border-slate-100 px-3 py-2">
          <h5 className="text-[11px] font-semibold text-slate-700">By hub</h5>
          <p className="text-[10.5px] text-slate-400">Named by nearest hub -- "At X" means it happened inside the fence, otherwise the average distance away is shown.</p>
        </div>
        {Object.keys(data.by_hub_proximity).length === 0 ? (
          <p className="px-3 py-4 text-center text-[11px] text-slate-400">No incidents with a resolvable position in this window.</p>
        ) : (
          <table className="w-full text-[11.5px]">
            <thead>
              <tr className="border-b border-slate-100 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                <th className="px-3 py-1.5 text-left">Hub</th>
                <th className="px-2 py-1.5 text-right">Incidents</th>
                <th className="px-2 py-1.5 text-right">Still down</th>
                <th className="px-2 py-1.5 text-right">Median TAT</th>
                <th className="px-2 py-1.5 text-right">Avg km away</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {Object.entries(data.by_hub_proximity)
                .sort(([, a], [, b]) => b.incidents - a.incidents)
                .map(([hub, r]) => (
                  <tr key={hub} className="transition-colors duration-100 hover:bg-slate-50/60">
                    <td className="px-3 py-1.5 font-medium text-slate-700">{hub}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">{r.incidents}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">
                      {r.still_open > 0 ? <span className="text-rose-600">{r.still_open}</span> : '—'}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">{r.median_hours ?? '—'}h</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">
                      {r.avg_km_away != null ? `${r.avg_km_away} km` : '—'}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
